import { z } from "zod";
import { ArchitectureNoteSchema, CostSourceSchema, ObservationSchema, RunMetricsSchema, } from "./finding.js";
import { EngineSchema, SeveritySchema } from "./rule.js";
export const ReviewStateSchema = z.enum(["queued", "running", "done", "failed", "skipped"]);
export const IN_FLIGHT_REVIEW_STATES = ["queued", "running"];
export function isReviewInFlight(state) {
    return IN_FLIGHT_REVIEW_STATES.includes(state);
}
export const SkipReasonSchema = z.enum([
    "diff_too_large",
    "disabled",
    "no_rules",
    "mr_closed",
    // A newer push arrived before this run finished; its head is stale.
    "superseded",
    "cancelled_by_user",
]);
export const REVIEW_CANCEL_QUERY_PARAM = "cancel";
export const ReviewPermissionsSchema = z.object({ canCancel: z.boolean() });
export const ReviewModeSchema = z.enum(["single", "fanout"]);
/**
 * What a review covers. "full" is a normal review: the line review, the scanner,
 * and — if the MR qualifies — the architecture step. "architecture" runs only the
 * architecture step: no line findings, no scan, nothing resolved. It exists so an
 * MR can be judged as a whole a second time, after the first review has already
 * used up the one automatic architecture pass.
 */
export const ReviewScopeSchema = z.enum(["full", "architecture"]);
export const ProjectDtoSchema = z.object({
    id: z.string().uuid(),
    gitlabProjectId: z.number().int(),
    pathWithNamespace: z.string(),
    enabled: z.boolean(),
    model: z.string(),
    /** null on any of these four: inherit the global setting. */
    maxDiffLines: z.number().int().positive().nullable(),
    fanOutAboveLines: z.number().int().nonnegative().nullable(),
    maxSubagents: z.number().int().positive().nullable(),
    subagentModel: z.string().nullable(),
    /** Model of the architecture step; null falls back to the project's model. */
    architectureModel: z.string().nullable(),
    groups: z.array(z.string()),
    reviewCount: z.number().int().nonnegative(),
    findingCount: z.number().int().nonnegative(),
});
export const OpenRouterModelSchema = z.object({
    id: z.string(),
    name: z.string(),
    contextLength: z.number().int().nullable(),
});
export const UpdateProjectSchema = z.object({
    enabled: z.boolean().optional(),
    gitlabProjectId: z.number().int().positive().optional(),
    model: z.string().min(1).optional(),
    maxDiffLines: z.number().int().positive().nullable().optional(),
    fanOutAboveLines: z.number().int().nonnegative().nullable().optional(),
    maxSubagents: z.number().int().positive().nullable().optional(),
    subagentModel: z.string().min(1).nullable().optional(),
    architectureModel: z.string().min(1).nullable().optional(),
    groups: z.array(z.string()).optional(),
});
/**
 * Global defaults. Every field is nullable: null means "the environment value
 * still applies", so a deployment keeps working without a settings row and an
 * operator can hand a field back to env by clearing it.
 */
export const SettingsSchema = z.object({
    defaultMaxDiffLines: z.number().int().positive().nullable(),
    defaultModel: z.string().min(1).nullable(),
    agentTimeoutSeconds: z.number().int().positive().nullable(),
    fanOutAboveLines: z.number().int().nonnegative().nullable(),
    maxSubagents: z.number().int().positive().nullable(),
    subagentModel: z.string().min(1).nullable(),
    architectureModel: z.string().min(1).nullable(),
    /**
     * Below this many changed lines the architecture step is skipped: a typo fix
     * needs no verdict on its approach, and the step costs real money.
     */
    architectureMinLines: z.number().int().nonnegative().nullable(),
});
export const UpdateSettingsSchema = SettingsSchema.partial();
/**
 * What the settings screen renders: the stored overrides plus the env values
 * they fall back to, so a cleared field can show its inherited value greyed out.
 */
export const SettingsDtoSchema = z.object({
    stored: SettingsSchema,
    fallback: z.object({
        defaultMaxDiffLines: z.number().int().positive(),
        defaultModel: z.string(),
        agentTimeoutSeconds: z.number().int().positive(),
        fanOutAboveLines: z.number().int().nonnegative(),
        maxSubagents: z.number().int().positive(),
        architectureMinLines: z.number().int().nonnegative(),
    }),
});
export const SetOverrideSchema = z.object({
    enabled: z.boolean().nullable(),
    severity: SeveritySchema.nullable(),
});
/** A rule as shown in the UI, with its effective state for one project. */
export const ProjectRuleDtoSchema = z.object({
    id: z.string(),
    group: z.string(),
    title: z.string(),
    engine: EngineSchema,
    scope: z.array(z.string()),
    tags: z.array(z.string()),
    body: z.string(),
    filePath: z.string(),
    defaultSeverity: SeveritySchema,
    defaultEnabled: z.boolean(),
    effectiveSeverity: SeveritySchema,
    effectiveEnabled: z.boolean(),
    overridden: z.boolean(),
});
export const FindingVoteSchema = z.enum(["up", "down"]);
export const FeedbackSourceSchema = z.enum(["reaction"]);
export const RuleVotesSchema = z.object({
    up: z.number().int().nonnegative(),
    down: z.number().int().nonnegative(),
});
export const RuleDtoSchema = z.object({
    id: z.string(),
    group: z.string(),
    title: z.string(),
    severity: SeveritySchema,
    engine: EngineSchema,
    scope: z.array(z.string()),
    tags: z.array(z.string()),
    enabled: z.boolean(),
    body: z.string(),
    filePath: z.string(),
    activeInProjects: z.number().int().nonnegative(),
    votes: RuleVotesSchema,
});
export const SyncStatusSchema = z.object({
    syncedAt: z.string().nullable(),
    ruleCount: z.number().int().nonnegative(),
    groups: z.array(z.string()),
    errors: z.array(z.string()),
    /** Browsable rules repo root, credentials stripped. Null if not derivable. */
    repoWebUrl: z.string().nullable(),
    repoRef: z.string(),
});
export const ReviewDtoSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    projectPath: z.string(),
    mrIid: z.number().int(),
    reviewedSha: z.string(),
    previousSha: z.string().nullable(),
    state: ReviewStateSchema,
    skipReason: SkipReasonSchema.nullable(),
    pipelineId: z.number().int().nullable(),
    model: z.string(),
    mode: ReviewModeSchema,
    /** Reviews from before the architecture-only run existed are all "full". */
    scope: ReviewScopeSchema.default("full"),
    subagentCount: z.number().int().nonnegative().nullable(),
    durationS: z.number().int().nullable(),
    costUsd: z.number().nullable(),
    /** Null on reviews from before cost measurement was tracked. */
    costSource: CostSourceSchema.nullable().default(null),
    findingCount: z.number().int().nonnegative(),
    observationCount: z.number().int().nonnegative(),
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
    error: z.string().nullable(),
});
export const ReviewDetailDtoSchema = ReviewDtoSchema.extend({
    findings: z.array(z.object({
        ruleId: z.string(),
        severity: SeveritySchema,
        file: z.string(),
        line: z.number().int(),
        message: z.string(),
        delivered: z.enum(["inline", "summary"]),
    })),
    observations: z.array(ObservationSchema),
    architectureNotes: z.array(ArchitectureNoteSchema),
    metrics: RunMetricsSchema.nullable(),
});
export const CreateReviewSchema = z.object({
    projectId: z.string().uuid(),
    mrIid: z.number().int().positive(),
    /** Omitted means a full review — the behaviour every caller had before this existed. */
    scope: ReviewScopeSchema.default("full"),
});
export const StatsOverviewSchema = z.object({
    /**
     * Overdrive: raw MR activity per project over a fixed eight ISO weeks,
     * independent of the `days` window and of whether a review ever ran. Up to
     * five projects, the fastest by this week's code volume first.
     */
    velocity: z.object({
        streams: z.array(z.object({
            projectPath: z.string(),
            /** Always eight entries, oldest first, quiet weeks included as zeros. */
            weeks: z.array(z.object({
                /** Monday of the week, YYYY-MM-DD. */
                week: z.string(),
                opened: z.number().int(),
                merged: z.number().int(),
                changedLines: z.number().int(),
            })),
            /** The last entry of `weeks`, lifted out for the readout. */
            current: z.object({
                week: z.string(),
                opened: z.number().int(),
                merged: z.number().int(),
                changedLines: z.number().int(),
            }),
        })),
        /** Monday of the current week. */
        currentWeek: z.string(),
    }),
    reviewsPerDay: z.array(z.object({ day: z.string(), count: z.number().int() })),
    findingsBySeverity: z.array(z.object({ severity: SeveritySchema, count: z.number().int() })),
    topRules: z.array(z.object({ ruleId: z.string(), count: z.number().int() })),
    topProjects: z.array(z.object({ projectPath: z.string(), count: z.number().int() })),
    missingRules: z.array(z.object({ suggestedRule: z.string(), count: z.number().int() })),
    /** Per-model cost and speed over done reviews in the window. avgTokensPerSec is
     * effective throughput (tokens_out / whole-run duration incl. tool calls) and
     * null while no review of that model carries token counts yet. */
    models: z.array(z.object({
        model: z.string(),
        reviews: z.number().int(),
        costUsd: z.number(),
        avgCostUsd: z.number(),
        avgDurationS: z.number(),
        avgTokensPerSec: z.number().nullable(),
    })),
    totals: z.object({
        reviews: z.number().int(),
        findings: z.number().int(),
        observations: z.number().int(),
        costUsd: z.number(),
        avgCostUsd: z.number(),
        avgDurationS: z.number(),
    }),
    /**
     * What was actually paid in the window, failed runs included — `totals` counts
     * finished reviews only. `unmeasuredReviews` is how many of those runs did not
     * report a complete cost, which makes `costUsd` a lower bound rather than a
     * fact whenever it is greater than zero.
     */
    spend: z.object({
        costUsd: z.number(),
        failedCostUsd: z.number(),
        reviews: z.number().int(),
        measuredReviews: z.number().int(),
        unmeasuredReviews: z.number().int(),
    }),
});
export const SpendBucketSchema = z.enum(["day", "week", "month"]);
/**
 * Spend over time, on the same definition as `spend` above: done and failed runs
 * both, because both were billed. Buckets without a review carry a zero rather
 * than being left out, so the bars keep their distance to each other.
 */
export const SpendSeriesSchema = z.object({
    bucket: SpendBucketSchema,
    buckets: z.array(z.object({
        /** Start of the bucket: the day, its Monday, or the first of the month. */
        start: z.string(),
        costUsd: z.number(),
        reviews: z.number().int(),
    })),
});
/**
 * A secret gitleaks found outside any diff — see backlog/gitleaks-ausserhalb-des-diffs.md.
 *
 * `status` folds two independent facts into the one thing the UI sorts by:
 * "gone" is the scanner's verdict (the fingerprint stopped appearing),
 * "ignored"/"resolved" are a person's. A person's verdict wins over the
 * scanner's, because it is the one somebody has to stand behind.
 */
export const SecretStatusSchema = z.enum(["open", "ignored", "resolved", "gone"]);
export const SecretOutsideDiffDtoSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    projectPath: z.string(),
    file: z.string(),
    line: z.number().int(),
    description: z.string(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    status: SecretStatusSchema,
    dismissedComment: z.string().nullable(),
    dismissedBy: z.string().nullable(),
    dismissedAt: z.string().nullable(),
});
/**
 * A verdict on a rule suggestion. No comment here, unlike a secret: the two
 * outcomes ("wird eine Regel" / "wird keine") are self-explanatory, and the
 * suggestion is triage, not a security record somebody has to answer for.
 */
export const DismissRuleSuggestionSchema = z.object({
    /** The suggestion text; "" is the group of observations without one. */
    suggestion: z.string(),
    kind: z.enum(["ignored", "resolved"]),
});
/** A comment is mandatory: the point of dismissing is the reason, not the click. */
export const DismissSecretSchema = z.object({
    kind: z.enum(["ignored", "resolved"]),
    comment: z.string().trim().min(1).max(2000),
});
/**
 * Live progress of a running review. The pipeline streams these while it works;
 * the detail screen polls for them. Purely informational — a review whose events
 * never arrive is still a complete review, so nothing downstream may depend on
 * them.
 */
export const ReviewEventPhaseSchema = z.enum([
    "fetch",
    "gitleaks",
    "review",
    "architecture",
    "post",
]);
/**
 * "reasoning" is the model thinking out loud, by far the loudest kind — the UI
 * collapses it by default. "log" is the agent's own script output, "tool" a tool
 * or subagent call, "status" a phase boundary worth showing unfiltered.
 */
export const ReviewEventKindSchema = z.enum(["status", "log", "reasoning", "tool", "metric"]);
export const ReviewEventInputSchema = z.object({
    /** Monotonic per review, assigned by the agent — makes a retried batch idempotent. */
    seq: z.number().int().nonnegative(),
    phase: ReviewEventPhaseSchema,
    kind: ReviewEventKindSchema,
    level: z.enum(["info", "warn", "error"]).default("info"),
    message: z.string().max(8_000),
    data: z.record(z.unknown()).nullish(),
    /** Agent-side wall clock. The row also carries its arrival time. */
    at: z.coerce.date(),
});
export const AppendReviewEventsSchema = z.object({
    events: z.array(ReviewEventInputSchema).max(500),
});
export const ReviewEventDtoSchema = z.object({
    seq: z.number().int().nonnegative(),
    phase: ReviewEventPhaseSchema,
    kind: ReviewEventKindSchema,
    level: z.enum(["info", "warn", "error"]),
    message: z.string(),
    data: z.record(z.unknown()).nullable(),
    at: z.string(),
});
/** Context the agent needs to run one review. */
export const ReviewContextSchema = z.object({
    reviewId: z.string().uuid(),
    gitlabProjectId: z.number().int(),
    projectPath: z.string(),
    mrIid: z.number().int(),
    previousSha: z.string().nullable(),
    model: z.string(),
    /** "fanout" tells the agent to split the diff across subagents. */
    mode: ReviewModeSchema,
    /** "architecture" makes the pipeline skip the scanner and the line review. */
    scope: ReviewScopeSchema.default("full"),
    maxSubagents: z.number().int().positive(),
    /** Model the subagents run on; equals `model` unless overridden. */
    subagentModel: z.string(),
    agentTimeoutSeconds: z.number().int().positive(),
    /** Whether the architecture step runs for this review; decided at enqueue time. */
    architectureReview: z.boolean(),
    /**
     * The "review is running" note the backend posted on the MR. The pipeline edits
     * it into the final summary; null means it has to post a fresh comment instead.
     */
    startedNoteId: z.number().int().nullable(),
    /** Model the architecture step runs on; equals `model` unless overridden. */
    architectureModel: z.string(),
    /**
     * Wall clock the architecture step gets. It reads beyond the diff, so on a
     * large MR the reading alone is minutes — a short cap kills it mid-exploration
     * with nothing to show. Null on runs before this existed; the agent then uses
     * its own default.
     */
    architectureTimeoutSeconds: z.number().int().positive().nullable().default(null),
});
